// Phase 29 — multi-property comparison table.
//
// Renders a sticky-first-column side-by-side view from the JSON payload
// returned by POST /api/compare/deals. Sections (Property / Income / …)
// follow the order baked into compare_service.SECTIONS so the table layout
// mirrors the xlsx + pdf exports byte-for-byte.
//
// Props:
//   payload    — full server response
//   freeLimit  — when truthy, every column past index `freeLimit - 1` is
//                visually de-emphasised (used to warn free-tier users that
//                we only render the first N columns).

import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const CURRENCY_KEYS = new Set([
  "asking_price", "estimated_value", "purchase_price_paid", "monthly_rent",
  "other_income", "annual_taxes", "annual_insurance", "monthly_hoa",
  "monthly_utilities", "repair_budget", "purchase_price",
  "down_payment_amount", "loan_amount", "closing_costs", "transfer_tax",
  "arv", "refi_closing", "monthly_income", "monthly_expenses",
  "noi_monthly", "noi_annual", "cash_flow_monthly", "cash_flow_annual",
  "total_cash_invested", "suggested_offer", "total_returns_to_date",
]);
const PERCENT_KEYS = new Set([
  "vacancy_pct", "management_pct", "down_payment_pct", "interest_rate",
  "refi_ltv_pct", "refi_rate", "sale_costs_pct", "cap_rate",
  "cash_on_cash", "dscr", "irr_projected", "break_even_occupancy",
  "irr_to_date_pct", "cagr",
]);
const BOOL_KEYS = new Set(["is_owned", "is_cash_purchase"]);
// Phase 36 Batch Q — keys that should render "Never" when null and
// "X.Y yrs" when finite. Currently just payback; add other null-aware
// year-units here if more get introduced.
const NEVER_OR_YEARS_KEYS = new Set(["payback_period_years"]);

function fmt(value, key) {
  if (NEVER_OR_YEARS_KEYS.has(key)) {
    if (value === null || value === undefined) return "Never";
    const n = Number(value);
    if (!Number.isFinite(n)) return "Never";
    return `${n.toFixed(1)} yrs`;
  }
  if (value === null || value === undefined || value === "") return "—";
  if (BOOL_KEYS.has(key)) return value ? "Yes" : "No";
  if (CURRENCY_KEYS.has(key)) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  }
  if (PERCENT_KEYS.has(key)) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `${(n * 100).toFixed(2)}%`;
  }
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value).slice(0, 80);
  return String(value);
}

function scoreTone(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "bg-slate-100 text-slate-500";
  if (n >= 75) return "bg-emerald-100 text-emerald-800 font-bold";
  if (n >= 45) return "bg-amber-100 text-amber-800 font-bold";
  return "bg-rose-100 text-rose-800 font-bold";
}

function groupRows(summaryTable) {
  // Build [{section, rows: [{label, key, values}, …]}, …]
  const groups = [];
  let current = null;
  for (const row of summaryTable) {
    if (row.values === null) {
      // section header
      current = { section: row.section, rows: [] };
      groups.push(current);
    } else if (current) {
      current.rows.push(row);
    }
  }
  return groups;
}

export default function CompareTable({ payload, freeLimit }) {
  const { deals = [], summary_table: summaryTable = [], best_pick: bestPick } = payload || {};
  const groups = useMemo(() => groupRows(summaryTable), [summaryTable]);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggle = (section) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  if (!deals.length) {
    return <div className="text-sm text-slate-500" data-testid="compare-table-empty">No deals selected.</div>;
  }

  const limit = freeLimit ? Math.min(freeLimit, deals.length) : deals.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="compare-table">
      {bestPick && (
        <div className="flex items-start gap-3 rounded-t-2xl border-b border-slate-200 bg-indigo-50/70 p-4" data-testid="compare-best-pick">
          <span className="rounded-full bg-indigo-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Best pick</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{bestPick.address_short}</p>
            <p className="mt-0.5 text-xs text-slate-600">{bestPick.reason}</p>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr>
              <th className="sticky left-0 z-20 w-56 border-b border-r border-slate-200 bg-slate-100 px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Metric
              </th>
              {deals.map((d, i) => (
                <th
                  key={d.deal_id}
                  className={`min-w-[180px] border-b border-slate-200 bg-slate-50 px-3 py-3 text-left text-xs font-semibold ${
                    i >= limit ? "opacity-40" : ""
                  }`}
                  data-testid={`compare-col-${i}`}
                >
                  <div className="truncate text-slate-900">{d.address_short || `Deal ${i + 1}`}</div>
                  <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                    {(d.deal_type || "—").toUpperCase()} · {d.verdict || "—"}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const isCollapsed = collapsed.has(g.section);
              return (
                <Fragment key={`s-${g.section}`}>
                  <tr className="bg-slate-50">
                    <td
                      colSpan={deals.length + 1}
                      className="sticky left-0 z-10 cursor-pointer border-b border-slate-200 bg-slate-50 px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-indigo-700"
                      onClick={() => toggle(g.section)}
                      data-testid={`compare-section-${g.section.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {g.section}
                      </span>
                    </td>
                  </tr>
                  {!isCollapsed &&
                    g.rows.map((row, ridx) => (
                      <tr key={`${g.section}-${row.key}-${ridx}`} className="hover:bg-slate-50/60">
                        <td className="sticky left-0 z-10 w-56 border-b border-r border-slate-100 bg-white px-4 py-2 text-xs font-medium text-slate-700">
                          {row.label}
                        </td>
                        {(row.values || []).map((v, ci) => {
                          const muted = ci >= limit;
                          const isScore = row.key === "deal_score";
                          return (
                            <td
                              key={ci}
                              className={`border-b border-slate-100 px-3 py-2 text-sm align-top ${
                                muted ? "opacity-40" : ""
                              }`}
                              data-testid={`compare-cell-${row.key}-${ci}`}
                            >
                              {isScore ? (
                                <span className={`inline-block rounded-md px-2 py-0.5 ${scoreTone(v)}`}>
                                  {v != null ? Math.round(Number(v)) : "—"}
                                </span>
                              ) : (
                                <span className="whitespace-pre-wrap text-slate-900">{fmt(v, row.key)}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
